import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import Popover from './Popover';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../store/authStore';
import { pendingDecisionKeys, resolvePendingDecision, type PendingDecisionId } from '../../pendingDecisions';

/**
 * D-125 — a quiet "ממתין להחלטה" sign next to a part of the site whose topic
 * still waits for a decision (OTE_ALIGNMENT/PENDING_MARKERS.md).
 *
 * - The entry (texts, active) comes from the ONE registry, `src/pendingDecisions.ts`.
 *   A closed or removed entry renders nothing.
 * - OTE staff only (DB identity from `/permissions/me`), never a customer and
 *   never under /portal.
 * - A small soft-orange ⚠ button. Hover (mouse) or keyboard focus shows the
 *   bubble; a tap / click pins it open; Escape or an outside press closes it.
 * - The bubble text is also in the DOM while closed (sr-only) and wired by
 *   `aria-describedby`, so a screen reader reads it when focus lands on the sign.
 */
export default function PendingDecision({ id, className = '' }: { id: PendingDecisionId; className?: string }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { ready, portalOnly, isOteEmployee, capabilities } = usePermissions();
  const loginIsOteEmployee = useAuthStore((s) => s.user?.is_ote_employee);
  const entry = resolvePendingDecision(id, {
    ready,
    portalOnly,
    serverIsOteEmployee: isOteEmployee,
    loginIsOteEmployee,
    inPortal: pathname === '/portal' || pathname.startsWith('/portal/'),
    isGeneralManager: capabilities.isGeneralManager, // D-301: `managerOnly` markers (PM-27)
  });

  const anchorRef = useRef<HTMLButtonElement>(null);
  const descId = useId();
  const titleId = useId();
  const panelId = useId();
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const skipFocus = useRef(false);

  if (!entry) return null;

  const keys = pendingDecisionKeys(entry.id);
  const open = hover || focused || pinned;
  const closeAll = () => { setHover(false); setFocused(false); setPinned(false); };

  const body = (
    <>
      <span className="block">
        <span className="font-semibold text-text">{t('pendingDecision.decide')}: </span>
        {t(keys.decide)}
      </span>
      <span className="mt-1 block">
        <span className="font-semibold text-text">{t('pendingDecision.current')}: </span>
        {t(keys.current)}
      </span>
    </>
  );

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        data-pending-decision={entry.id}
        aria-label={t('pendingDecision.label')}
        aria-describedby={descId}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHover(true); }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHover(false); }}
        onFocus={(e) => {
          if (skipFocus.current) { skipFocus.current = false; return; }
          // keyboard focus only: a tap focuses too, and the click below pins it
          let keyboard = true;
          try { keyboard = e.currentTarget.matches(':focus-visible'); } catch { /* older engine: treat as keyboard */ }
          if (keyboard) setFocused(true);
        }}
        onBlur={() => setFocused(false)}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPinned((v) => !v); setFocused(false); }}
        className={`inline-flex shrink-0 items-center justify-center rounded-full p-0.5 align-middle text-warning-600 opacity-70 transition-opacity duration-micro
          hover:opacity-100 aria-expanded:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${className}`}
      >
        <AlertTriangle size={14} strokeWidth={1.75} aria-hidden />
      </button>
      <span id={descId} className="sr-only">
        {t(keys.decide)} {t('pendingDecision.current')}: {t(keys.current)}
      </span>
      <Popover
        open={open}
        onClose={(reason) => { if (reason === 'escape') skipFocus.current = true; closeAll(); }}
        anchorRef={anchorRef}
        align="start"
        id={panelId}
        role="group"
        ariaLabelledBy={titleId}
        className="w-80 max-w-[calc(100vw-2rem)] p-3"
      >
        <p id={titleId} className="flex items-center gap-1.5 text-sm font-semibold text-warning-700">
          <AlertTriangle size={14} strokeWidth={1.75} aria-hidden />
          {t('pendingDecision.label')}
          <span className="ms-auto text-xs font-normal text-text-3" dir="ltr">{entry.topic}</span>
        </p>
        <p className="mt-2 text-sm leading-relaxed text-text-2">{body}</p>
      </Popover>
    </>
  );
}
